import { useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  addProtocols,
  setProtocols,
  setLoading,
  setError,
  setHasMore,
  setCurrentPage,
  setTotal,
  resetProtocols,
} from '../../../redux/slices/protocolSlice';
import { ProtocolCategory, ProtocolStatus } from '../../../types';
import apiService from '../../../services/api';

const LIMIT = 20;

export const useInfiniteProtocols = () => {
  const dispatch = useAppDispatch();
  const { protocols, filters, currentPage, hasMore, isLoading, error } = useAppSelector(
    state => state.protocols
  );

  const fetchProtocols = useCallback(
    async (page: number, reset = false) => {
      try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        // Use search endpoint if there's a search term, otherwise use regular getProtocols
        let response;
        if (filters.search && filters.search.trim()) {
          // Use dedicated search endpoint
          const searchResult = await apiService.searchProtocols({
            query: filters.search,
            page,
            limit: LIMIT,
            filters: {
              category: filters.category as ProtocolCategory | undefined,
              status: filters.status as ProtocolStatus | undefined,
            },
            sortBy: filters.sortBy as
              | 'title'
              | 'createdAt'
              | 'updatedAt'
              | 'viewCount'
              | 'usageCount'
              | undefined,
            sortOrder: filters.sortOrder as 'asc' | 'desc' | undefined,
          });
          response = {
            protocols: searchResult.protocols,
            pagination: searchResult.pagination,
          };
        } else {
          // Use regular getProtocols endpoint
          const params = {
            page,
            limit: LIMIT,
            category: filters.category || undefined,
            status: filters.status || undefined,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
          };
          response = await apiService.getProtocols(params);
        }

        // Debug log (remove in production)
        console.log('useInfiniteProtocols response:', {
          protocolsCount: response.protocols.length,
          pagination: response.pagination,
          reset,
        });

        if (reset) {
          dispatch(setProtocols(response.protocols));
        } else {
          dispatch(addProtocols(response.protocols));
        }

        dispatch(setTotal(response.pagination.total));
        dispatch(setCurrentPage(page));
        dispatch(setHasMore(response.pagination.hasNext || response.protocols.length === LIMIT));
      } catch (err: any) {
        dispatch(setError(err.message || 'Error al cargar protocolos'));
        dispatch(setHasMore(false));
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, filters]
  );

  const fetchMore = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchProtocols(currentPage + 1, false);
    }
  }, [isLoading, hasMore, currentPage, fetchProtocols]);

  // Track if initial load has been done and previous filters
  const initialLoadRef = useRef(false);
  const prevFiltersRef = useRef<string>(
    JSON.stringify({
      search: filters.search,
      category: filters.category,
      status: filters.status,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    })
  );

  // Initial load (only once)
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      fetchProtocols(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset and fetch when filters change (but not on initial mount)
  useEffect(() => {
    // Skip if initial load hasn't been done yet
    if (!initialLoadRef.current) {
      return;
    }

    // Check if filters actually changed by comparing JSON strings
    const currentFiltersString = JSON.stringify({
      search: filters.search,
      category: filters.category,
      status: filters.status,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    });

    if (prevFiltersRef.current !== currentFiltersString) {
      prevFiltersRef.current = currentFiltersString;
      dispatch(resetProtocols());
      fetchProtocols(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.category, filters.status, filters.sortBy, filters.sortOrder]);

  return {
    protocols,
    loading: isLoading,
    error,
    hasMore,
    fetchMore,
    currentPage,
  };
};

export {};
